import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Switch,
  Button,
  Card,
  Row,
  Col,
  Upload,
  message,
  Tooltip,
  Space,
  Typography,
  Avatar,
  Tag,
  InputNumber,
} from 'antd';
import {
  InfoCircleOutlined,
  UploadOutlined,
  SaveOutlined,
  CloseCircleOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import axios from "axios";
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';

const { Title, Text } = Typography;
const { Option } = Select;

const regionOptions = ["India", "China", "Global"];
const approverOptions = [
  "admin",
  "unithead",
  "peoplemanager",
  "employee",
  "special",
  "",
];

type RewardFormInputs = {
  rewardName: string;
  parentCategoryId: string;
  type: string;
  requiresApproval: string;
  isActive: boolean;
  eligibleToNominate: string;
  budget: number | "";
  regionName: string[];
  rolesList: string[];
  approver1: string;
  approver2: string;
  approver3: string;
  approver4: string;
  approver5: string;
  icon: UploadFile[];
};

const fieldHelp: Record<keyof RewardFormInputs, string> = {
  rewardName: "The display name of the reward.",
  parentCategoryId: "Group this reward under a parent category for hierarchy.",
  type: "Choose if this is an individual or team reward.",
  requiresApproval:
    "Does this reward require approval? 'false' = no approval, 'true' = approval needed, 'admin'/'global' = special flows.",
  isActive: "Toggle to enable or disable this reward.",
  eligibleToNominate:
    "'anyone' = all users can nominate. 'reportees' = only people managers for their direct reports.",
  budget: "Points or value associated with this reward.",
  regionName: "Select which regions/countries this reward is available in.",
  rolesList:
    "Only users with these roles can see or nominate for this reward. Use 'notVisibleToAllUsers' for confidential rewards.",
  approver1: "First approver in the workflow.",
  approver2: "Second approver in the workflow (optional).",
  approver3: "Third approver in the workflow (optional).",
  approver4: "Fourth approver in the workflow (optional).",
  approver5: "Fifth approver in the workflow (optional).",
  icon: "Upload an icon/image for this reward.",
};

const FieldLabel: React.FC<{ label: string; tooltip: string }> = ({ label, tooltip }) => (
  <Space>
    <span>{label}</span>
    <Tooltip title={tooltip}>
      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
    </Tooltip>
  </Space>
);


interface CategoryGroup {
  root: { rewardTypeId: string; rewardName: string };
  children: { rewardTypeId: string; rewardName: string }[];
}

export default function AdminRewardForm() {
  const [form] = Form.useForm();
  const [categoryGroups, setCategoryGroups] = useState<CategoryGroup[]>([]);
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { instance, accounts } = useMsal();

  const requiresApproval = Form.useWatch('requiresApproval', form);
  const formValues = Form.useWatch([], form) || {};

  // Build grouped structure using parentcategoryname field returned by the API
  function buildCategoryGroups(items: any[]): CategoryGroup[] {
    const roots = items
      .filter(i => !i.parentcategoryname || i.parentcategoryname === "No Parent")
      .sort((a, b) => a.rewardName.localeCompare(b.rewardName));
    return roots.map(root => ({
      root: { rewardTypeId: root.rewardTypeId, rewardName: root.rewardName },
      children: items
        .filter(i => i.parentcategoryname === root.rewardName)
        .sort((a, b) => a.rewardName.localeCompare(b.rewardName))
        .map(c => ({ rewardTypeId: c.rewardTypeId, rewardName: c.rewardName })),
    }));
  }

  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    return res.idToken;
  };

  // Fetch parent categories and roles from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await fetchToken();
        const [rewardsRes, rolesRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/Rewards/list`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/Roles`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setCategoryGroups(buildCategoryGroups(rewardsRes.data));
        const fetchedRoles: string[] = rolesRes.data.map((r: any) => r.customRoleName);
        if (!fetchedRoles.includes('notVisibleToAllUsers')) {
          fetchedRoles.push('notVisibleToAllUsers');
        }
        setRoleOptions(fetchedRoles);
      } catch (err) {
        setCategoryGroups([]);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, []);

  // Form submission handler
  const onSubmit = async (values: any) => {
    setLoading(true);
    const formData = new FormData();
    
    Object.entries(values).forEach(([key, value]) => {
      if (key === "icon" && fileList.length > 0 && fileList[0].originFileObj) {
        formData.append("Icon", fileList[0].originFileObj);
      } else if (Array.isArray(value)) {
        value.forEach((v) => formData.append(key, v));
      } else if (value !== undefined && value !== null) {
        formData.append(key, value as any);
      }
    });

    try {
      const token = await fetchToken();
      await axios.post(`${process.env.REACT_APP_API_URL}/api/Rewards/addAward`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      message.success("Reward created successfully!");
      form.resetFields();
      setFileList([]);
      setIconPreview(null);
    } catch (err) {
      message.error("Failed to submit reward.");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadChange = ({ fileList: newFileList }: any) => {
    setFileList(newFileList);
    if (newFileList.length > 0 && newFileList[0].originFileObj) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setIconPreview(e.target?.result as string);
      };
      reader.readAsDataURL(newFileList[0].originFileObj);
    } else {
      setIconPreview(null);
    }
  };

  const uploadProps = {
    beforeUpload: (file: File) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
      }
      return false; // Prevent auto upload
    },
    maxCount: 1,
    fileList,
    onChange: handleUploadChange,
  };

  return (
    <div style={{ width: '100%', margin: '0px auto', padding: '0 0px' }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        initialValues={{
          type: 'individual',
          requiresApproval: 'false',
          isActive: true,
          eligibleToNominate: 'anyone',
          regionName: [],
          rolesList: [],
          parentCategoryId: '',
        }}
        requiredMark="optional"
      >
        <Card 
          title="Basic Information" 
          bordered={false}
          style={{ marginBottom: 24, boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)' }}
        >
          <Row gutter={[24, 8]}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="rewardName"
                label={<FieldLabel label="Reward Name" tooltip={fieldHelp.rewardName} />}
                rules={[{ required: true, message: 'Please enter reward name' }]}
              >
                <Input placeholder="Enter reward name" size="large" />
              </Form.Item>
            </Col>
            
            <Col xs={24} lg={12}>
              <Form.Item
                name="parentCategoryId"
                label={<FieldLabel label="Parent Category" tooltip={fieldHelp.parentCategoryId} />}
              >
                <Select
                  placeholder="Select a parent award (optional)"
                  allowClear
                  showSearch
                  size="large"
                  style={{ width: '100%' }}
                  filterOption={(input, option) =>
                    String(option?.children ?? option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  <Option value="">None</Option>
                  {categoryGroups.map(({ root, children }) =>
                    children.length > 0 ? (
                      <Select.OptGroup key={root.rewardTypeId} label={root.rewardName}>
                        <Option value={root.rewardTypeId}>
                          {root.rewardName}
                        </Option>
                        {children.map(child => (
                          <Option key={child.rewardTypeId} value={child.rewardTypeId}>
                            └ {child.rewardName}
                          </Option>
                        ))}
                      </Select.OptGroup>
                    ) : (
                      <Option key={root.rewardTypeId} value={root.rewardTypeId}>
                        {root.rewardName}
                      </Option>
                    )
                  )}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                name="type"
                label={<FieldLabel label="Type" tooltip={fieldHelp.type} />}
              >
                <Select size="large" placeholder="Select type">
                  <Option value="individual">Individual</Option>
                  <Option value="team">Team</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                name="requiresApproval"
                label={<FieldLabel label="Requires Approval" tooltip={fieldHelp.requiresApproval} />}
              >
                <Select size="large" placeholder="Select approval type">
                  <Option value="false">No</Option>
                  <Option value="true">Yes</Option>
                  <Option value="admin">Admin</Option>
                  <Option value="global">Global</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                name="isActive"
                label={<FieldLabel label="Is Active" tooltip={fieldHelp.isActive} />}
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="Active" 
                  unCheckedChildren="Inactive"
                  size="default"
                  style={{ width: 100 }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="eligibleToNominate"
                label={<FieldLabel label="Eligible To Nominate" tooltip={fieldHelp.eligibleToNominate} />}
              >
                <Select size="large" placeholder="Select eligibility">
                  <Option value="anyone">Anyone</Option>
                  <Option value="reportees">People Managers</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="budget"
                label={<FieldLabel label="Budget" tooltip={fieldHelp.budget} />}
              >
                <InputNumber
                  placeholder="Enter budget"
                  size="large"
                  min={0}
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value?.replace(/,/g, '') as any}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card 
          title="Configuration" 
          bordered={false}
          style={{ marginBottom: 24, boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)' }}
        >
          <Row gutter={[24, 8]}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="regionName"
                label={<FieldLabel label="Region Name" tooltip={fieldHelp.regionName} />}
              >
                <Select
                  mode="tags"
                  size="large"
                  placeholder="Select or type regions"
                  tokenSeparators={[',']}
                  options={regionOptions.map(r => ({ label: r, value: r.toLowerCase() }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="rolesList"
                label={<FieldLabel label="Roles List" tooltip={fieldHelp.rolesList} />}
                extra={<Text type="secondary" style={{ fontSize: 12 }}>Use <Text strong>notVisibleToAllUsers</Text> for confidential rewards</Text>}
              >
                <Select
                  mode="tags"
                  size="large"
                  placeholder="Select or type roles"
                  tokenSeparators={[',']}
                  options={roleOptions.map(r => ({ label: r, value: r }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                name="icon"
                label={<FieldLabel label="Icon" tooltip={fieldHelp.icon} />}
              >
                <Upload {...uploadProps} listType="picture">
                  <Button icon={<UploadOutlined />} size="large">
                    Choose File
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {requiresApproval !== 'false' && (
          <Card 
            title="Approval Workflow" 
            bordered={false}
            style={{ marginBottom: 24, boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)' }}
          >
            <Row gutter={[24, 8]}>
              {[1, 2, 3, 4, 5].map((num) => (
                <Col xs={24} sm={12} lg={8} key={num}>
                  <Form.Item
                    name={`approver${num}`}
                    label={
                      <FieldLabel 
                        label={`Approver ${num}`} 
                        tooltip={fieldHelp[`approver${num}` as keyof RewardFormInputs]} 
                      />
                    }
                  >
                    <Select size="large" placeholder={`Select approver ${num}`} allowClear>
                      {approverOptions.map((role) => (
                        <Option key={role || 'none'} value={role}>
                          {role || 'None'}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Card>
        )}

        <Card 
          bordered={false}
          style={{ marginBottom: 24, boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)' }}
        >
          <Space size="middle">
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
              size="large"
            >
              Save Reward
            </Button>
            <Button
              htmlType="reset"
              icon={<CloseCircleOutlined />}
              size="large"
              onClick={() => {
                form.resetFields();
                setFileList([]);
                setIconPreview(null);
              }}
            >
              Cancel
            </Button>
          </Space>
        </Card>
      </Form>

      {/* Live Preview */}
      <Card 
        title={
          <Space>
            <TrophyOutlined />
            <span>Reward Preview</span>
          </Space>
        }
        bordered={false}
        style={{ boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)' }}
      >
        <Row gutter={24} align="middle">
          <Col>
            {iconPreview ? (
              <Avatar src={iconPreview} size={80} shape="square" />
            ) : (
              <Avatar size={80} shape="square" style={{ backgroundColor: '#1890ff' }}>
                {formValues.rewardName?.[0]?.toUpperCase() || '?'}
              </Avatar>
            )}
          </Col>
          <Col flex="auto">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Title level={4} style={{ margin: 0 }}>
                {formValues.rewardName || 'Reward Name'}
              </Title>
              
              <Space wrap>
                <Tag color="blue">{formValues.type?.toUpperCase() || 'INDIVIDUAL'}</Tag>
                <Tag color={formValues.isActive ? 'green' : 'red'}>
                  {formValues.isActive ? 'ACTIVE' : 'INACTIVE'}
                </Tag>
                {formValues.requiresApproval !== 'false' && (
                  <Tag color="orange">REQUIRES APPROVAL</Tag>
                )}
              </Space>

              <div style={{ marginTop: 8 }}>
                {formValues.regionName && formValues.regionName.length > 0 && (
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary">Regions: </Text>
                    <Text>{formValues.regionName.join(', ')}</Text>
                  </div>
                )}
                
                {formValues.rolesList && formValues.rolesList.length > 0 && (
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary">Roles: </Text>
                    <Text>{formValues.rolesList.join(', ')}</Text>
                  </div>
                )}
                
                {formValues.budget && (
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary">Budget: </Text>
                    <Text strong>{formValues.budget}</Text>
                  </div>
                )}

                <div style={{ marginBottom: 4 }}>
                  <Text type="secondary">Eligible To Nominate: </Text>
                  <Text>{formValues.eligibleToNominate || 'anyone'}</Text>
                </div>

                {requiresApproval !== 'false' && (
                  <div>
                    <Text type="secondary">Approval Chain: </Text>
                    <Text>
                      {[
                        formValues.approver1,
                        formValues.approver2,
                        formValues.approver3,
                        formValues.approver4,
                        formValues.approver5
                      ]
                        .filter(Boolean)
                        .join(' → ') || 'None configured'}
                    </Text>
                  </div>
                )}
              </div>
            </Space>
          </Col>
        </Row>
      </Card>
    </div>
  );
}